import { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

// Renders the ~23k fine sub-cells on a canvas and provides a single
// mousemove hover (lattice math -> sub-cell) with a highlight rectangle.
export default function FineLayer({ bundle, colorOf, outline, hoverHtml, visibleOf }) {
  const map = useMap();

  useEffect(() => {
    if (!bundle) return;
    const { geom, fidx } = bundle;
    const feats = [];
    for (let p = 0; p < fidx.length; p++) {
      if (visibleOf && !visibleOf(fidx[p])) continue;
      feats.push({
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [geom[p]] },
        properties: { _f: colorOf(fidx[p]) },
      });
    }
    const renderer = L.canvas({ padding: 0.5 });
    const layer = L.geoJSON(
      { type: "FeatureCollection", features: feats },
      {
        renderer,
        interactive: false,
        style: (f) => ({
          fillColor: f.properties._f,
          color: outline ? "#b9c6d6" : f.properties._f,
          weight: outline ? 0.3 : 0,
          opacity: outline ? 0.5 : 1,
          fillOpacity: 0.82,
        }),
      }
    ).addTo(map);
    return () => map.removeLayer(layer);
  }, [bundle, colorOf, outline, map]);

  useEffect(() => {
    if (!bundle || !hoverHtml) return;
    const { meta } = bundle;
    const NS = meta.nsub;
    const S2 = NS * NS;
    const SM = L.Projection.SphericalMercator;
    const tip = L.DomUtil.create("div", "cidp-tip");
    tip.style.display = "none";
    map.getContainer().appendChild(tip);
    const hl = L.rectangle([[0, 0], [0, 0]], { color: "#008eff", weight: 1.6, fill: false, interactive: false });
    let hlOn = false;
    const hide = () => {
      tip.style.display = "none";
      if (hlOn) { map.removeLayer(hl); hlOn = false; }
    };
    const onMove = (e) => {
      const pt = SM.project(e.latlng);
      const col = Math.floor((pt.x - meta.x0) / meta.step);
      const row = Math.floor((pt.y - meta.y0) / meta.step);
      if (col < 0 || col >= meta.ncol || row < 0 || row >= meta.nrow) return hide();
      let sc = Math.floor((pt.x - (meta.x0 + col * meta.step)) / meta.sub);
      let sr = Math.floor((pt.y - (meta.y0 + row * meta.step)) / meta.sub);
      sc = Math.max(0, Math.min(NS - 1, sc));
      sr = Math.max(0, Math.min(NS - 1, sr));
      const slot = (row * meta.ncol + col) * S2 + sr * NS + sc;
      const html = hoverHtml(slot);
      if (!html) return hide();
      tip.innerHTML = html;
      const sx = meta.x0 + col * meta.step + sc * meta.sub;
      const sy = meta.y0 + row * meta.step + sr * meta.sub;
      const sw = SM.unproject(L.point(sx, sy));
      const ne = SM.unproject(L.point(sx + meta.sub, sy + meta.sub));
      hl.setBounds([[sw.lat, sw.lng], [ne.lat, ne.lng]]);
      if (!hlOn) { hl.addTo(map); hlOn = true; }
      const cp = e.containerPoint;
      const cw = map.getContainer().clientWidth;
      const ch = map.getContainer().clientHeight;
      const tw = tip.offsetWidth || 220;
      const th = tip.offsetHeight || 160;
      let x = cp.x + 14;
      let y = cp.y + 14;
      if (x + tw > cw) x = cp.x - tw - 14;
      if (y + th > ch) y = cp.y - th - 14;
      if (x < 0) x = 2;
      if (y < 0) y = 2;
      tip.style.left = x + "px";
      tip.style.top = y + "px";
      tip.style.display = "block";
    };
    map.on("mousemove", onMove);
    map.on("mouseout", hide);
    return () => {
      map.off("mousemove", onMove);
      map.off("mouseout", hide);
      hide();
      tip.remove();
    };
  }, [bundle, hoverHtml, map]);

  return null;
}
